/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import Cookies from 'universal-cookie';
import { IProductAttributesRecId } from '../themes/dobbies/utilities/custom-types';
import { IUnbxdProduct } from '../modules/dobbies-unbxd-recommendations/types/unbxd-product';
import {
    getDeliveryOptionsAsync,
    getEstimatedAvailabilityAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    IProductInventoryInformation,
    mapProductInventoryInformation
} from '../themes/dobbies/utilities/inventory/product-inventory-utils';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';

export interface IWidgetData {
    count: number;
    widgetId: string;
    widgetPlacementId: string;
    widgetTitle: string;
    recommendations: IUnbxdProduct[];
}

export interface IGetUnbxdRecommendationData {
    unbxdRequestId: string;
    unbxdRecs: IWidgetData;
}

export interface ICategoryLevelNamesData {
    categoryLevelName?: string;
}

export class GetUnbxdRecommendationsDataInput implements Msdyn365.IActionInput {
    public pageType?: string | undefined;
    public widgetNumber?: string | undefined;
    public productID?: string | undefined;
    public catLevels?: ICategoryLevelNamesData[] | undefined;
    public cartItemIDs?: (string | undefined)[] | undefined;
    public brand?: string;
    private currentCategories: string;

    constructor(
        pageType?: string,
        widgetNumber?: string,
        productID?: string,
        catLevels?: ICategoryLevelNamesData[],
        cartItemIDs?: (string | undefined)[],
        brand?: string
    ) {
        this.pageType = pageType;
        this.widgetNumber = widgetNumber;
        this.productID = productID;
        this.catLevels = catLevels;
        this.cartItemIDs = cartItemIDs;
        this.currentCategories = '';
        this.brand = brand;

        catLevels?.forEach(catLevel => {
            this.currentCategories = catLevel?.categoryLevelName
                ? this.currentCategories.concat(`${catLevel.categoryLevelName}-`)
                : this.currentCategories;
        });
    }

    public getCacheKey = () => `Unbxd-recommendation-data-${this.pageType}-${this.widgetNumber}-${this.currentCategories}`;
    public getCacheObjectType = () => 'Unbxd-recommendation-data';
    public dataCacheType = (): Msdyn365.CacheType => 'request';
}

async function getUnbxdRecommendationData(
    input: GetUnbxdRecommendationsDataInput,
    ctx: Msdyn365.IActionContext
): Promise<IGetUnbxdRecommendationData | undefined> {
    const cookies = new Cookies();
    const unbxdUid = cookies.get('unbxd.userId');
    const pageType = input?.pageType ? input.pageType.toUpperCase() : '';
    const productID = input?.productID || '';
    const catLevels = input?.catLevels || [];
    const cartItemIDs = input?.cartItemIDs || [];
    const unbxdApiKey = ctx.requestContext.app.config.unbxdApiKey;
    const unbxdSiteKey = ctx.requestContext.app.config.unbxdSiteKey;
    const brand = input.brand || '';

    if (!pageType) {
        return;
    }

    try {
        let unbxdUrl = '';

        switch (pageType) {
            case 'HOME':
                unbxdUrl = `https://recommendations.unbxd.io/v3.0/${unbxdApiKey}/${unbxdSiteKey}/recommendation?pageType=${pageType}&uid=${unbxdUid}`;
                break;

            case 'CATEGORY':
                const catnames = catLevels
                    .map((catName, index) => `&catlevel${index + 1}Name=${catName ? encodeURIComponent(catName.categoryLevelName!) : ''}`)
                    .join('');
                unbxdUrl = `https://recommendations.unbxd.io/v3.0/${unbxdApiKey}/${unbxdSiteKey}/recommendation?pageType=${pageType}${catnames}&uid=${unbxdUid}`;
                break;

            case 'BRAND':
                unbxdUrl = `https://recommendations.unbxd.io/v3.0/${unbxdApiKey}/${unbxdSiteKey}/recommendation?pageType=${pageType}&brand=${brand}&uid=${unbxdUid}`;
                break;

            case 'PRODUCT':
                unbxdUrl = `https://recommendations.unbxd.io/v3.0/${unbxdApiKey}/${unbxdSiteKey}/recommendation?pageType=${pageType}&id=${productID}&uid=${unbxdUid}`;
                break;

            case 'CART':
                const productitemIds = cartItemIDs.map(itemId => `&id=${itemId}`).join('');
                unbxdUrl = `https://recommendations.unbxd.io/v3.0/${unbxdApiKey}/${unbxdSiteKey}/recommendation?pageType=${pageType}${productitemIds}&uid=${unbxdUid}`;
                break;

            default:
                unbxdUrl = '';
        }

        const response = await Msdyn365.sendRequest<any>(unbxdUrl, 'get');

        if (response.data && response.headers) {
            const widgets: IWidgetData[] = response.data.response.widgets;
            const recommendationsObj = widgets.find(widget => widget.widgetId.toLowerCase() === input.widgetNumber!.toLowerCase());

            if (recommendationsObj) {
                const mappedRecs: IUnbxdProduct[] = recommendationsObj.recommendations.map(product => {
                    return {
                        ...product,
                        AttributeValues: mapProductAttributes(product, ctx)
                    };
                });

                const productsWithDeliveryOptions = await getProductsDeliveryOptions(mappedRecs, ctx);

                const productsWithInventory = await getProductsInventory(productsWithDeliveryOptions, ctx);

                recommendationsObj.recommendations = productsWithInventory;

                // eslint-disable-next-line security/detect-object-injection
                const requestIdHeader: string = response.headers['x-request-id'] || '';
                const requestIds = requestIdHeader
                    .split(',')
                    .map(requestId => requestId.trim())
                    .filter(requestId => requestId.length > 0);

                return {
                    unbxdRequestId: Array.from(new Set(requestIds)).join(', '),
                    unbxdRecs: recommendationsObj
                };
            }
        }
        return;
    } catch {
        return;
    }
}

async function getProductsDeliveryOptions(products: IUnbxdProduct[], ctx: Msdyn365.IActionContext): Promise<IUnbxdProduct[]> {
    let productsWithDeliveryOptions: IUnbxdProduct[] = [...products];
    try {
        const productIds = products.map(product => product.RecordId);
        const deliveryOptions = await getDeliveryOptionsAsync({ callerContext: ctx, queryResultSettings: {} }, productIds);
        productsWithDeliveryOptions = products.map(product => {
            const productDeliveryOptions = deliveryOptions.find(option => option.ProductId === product.RecordId);
            return {
                ...product,
                DeliveryOptions: productDeliveryOptions?.DeliveryOptions || []
            };
        });
    } catch (error) {
        ctx.telemetry.error(`Error fetching delivery options: ${error}`);
    }
    return productsWithDeliveryOptions;
}

async function getProductsInventory(products: IUnbxdProduct[], ctx: Msdyn365.IActionContext): Promise<IUnbxdProduct[]> {
    try {
        const productsWithVariantIds = [...products];
        for (const product of productsWithVariantIds) {
            const variantId = product.variantId;
            // If the product has a variant ID (Kit Products)
            if (variantId) {
                product.RecordId = +variantId || product.RecordId;
            }
        }

        const productIds = productsWithVariantIds.map(product => product.RecordId);
        // Fetch Home Delivery Inventory (default warehouse)
        const homeDeliveryInventory = await getEstimatedAvailabilityAsync(
            { callerContext: ctx },
            {
                ProductIds: productIds,
                DefaultWarehouseOnly: true,
                FilterByChannelFulfillmentGroup: false
            }
        );
        let productsWithInventory: IUnbxdProduct[] = [...products];
        const mappedProductInventory = mapProductInventoryInformation(
            ctx,
            homeDeliveryInventory.ProductWarehouseInventoryAvailabilities || []
        );
        productsWithInventory = productsWithInventory.map(product => {
            const isVendorShipAttribute = product.AttributeValues?.find((attribute: AttributeValue) => {
                const attributeName = attribute.Name?.trim()?.toLowerCase();
                return attributeName === 'isvendershipproduct';
            });
            if (isVendorShipAttribute?.TextValue?.toLowerCase() === 'yes') {
                const vendorProductInventory: IProductInventoryInformation = {
                    ProductAvailableQuantity: {
                        ProductId: product.RecordId,
                        AvailableQuantity: 9999
                    },
                    IsProductAvailable: true
                };
                return {
                    ...product,
                    ProductHomeDeliveryInventory: vendorProductInventory
                };
            }
            const productInventory = mappedProductInventory.find(
                inventory => inventory.ProductAvailableQuantity.ProductId === product.RecordId
            );

            return {
                ...product,
                ProductHomeDeliveryInventory: productInventory || undefined
            };
        });

        // fetch Click and Collect Inventory (all warehouses)
        const cncProductAvailabilities = await getEstimatedAvailabilityAsync(
            { callerContext: ctx },
            { ProductIds: productIds, DefaultWarehouseOnly: false, FilterByChannelFulfillmentGroup: true }
        );

        const productsWithCncInventory = productsWithInventory.map(product => {
            const cncInventory = cncProductAvailabilities.AggregatedProductInventoryAvailabilities?.find(productInventory => {
                return productInventory.ProductId === product.RecordId && productInventory.PhysicalAvailableInventoryLevelCode !== 'OOS';
            });

            return {
                ...product,
                ProductClickAndCollectInventory: cncInventory || undefined
            };
        });

        return productsWithCncInventory;
    } catch (error) {
        ctx.telemetry.error(`Error fetching product inventory: ${error}`);
        return products;
    }
}

function mapProductAttributes(product: IUnbxdProduct, ctx: Msdyn365.IActionContext) {
    return ctx.requestContext.app.config.productAttributesRecIds?.map((attr: IProductAttributesRecId) => {
        const key = `Attr_${attr.recordId}`;
        const value = product[key];

        return {
            RecordId: attr.recordId,
            Name: attr.name,
            TextValue: value
        };
    });
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetUnbxdRecommendationData>>getUnbxdRecommendationData,
    id: 'getUnbxdRecommendationData'
});
